import { useState, useRef } from "react";
import {
    Grid, Card, Icon, CircularProgress, Chip, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { postUpload, postUploadFile } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useMaterialUIController } from "context";

export default function ComparativoBase() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [cols1, setCols1] = useState([]);
    const [cols2, setCols2] = useState([]);
    const [contratoCol1, setContratoCol1] = useState("");
    const [contratoCol2, setContratoCol2] = useState("");

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [result, setResult] = useState(null);
    const [selectedTab, setSelectedTab] = useState("ALL"); // "ALL" | "NOVO" | "DEVOLVIDO" | "MANTIDO_ACORDO" | "MANTIDO_REMESSA" | "REATIVADO"
    const [searchTerm, setSearchTerm] = useState("");

    const file1InputRef = useRef(null);
    const file2InputRef = useRef(null);

    const handleFile1Change = (selectedFile) => {
        setFile1(selectedFile || null);
        setCols1([]);
        setContratoCol1("");
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        postUpload("/relatorios/comparativo-base/colunas", formData)
            .then(resCols => {
                const list = resCols || [];
                setCols1(list);
                // Auto-detect default contract column
                const match = list.find(c => {
                    const lc = c.toLowerCase();
                    return lc.includes("contrato") || lc.includes("titulo") || lc.includes("seu_numero") || lc.includes("operacao") || lc.includes("ficha");
                });
                if (match) setContratoCol1(match);
                else if (list.length > 0) setContratoCol1(list[0]);
            })
            .catch(() => {});
    };

    const handleFile2Change = (selectedFile) => {
        setFile2(selectedFile || null);
        setCols2([]);
        setContratoCol2("");
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        postUpload("/relatorios/comparativo-base/colunas", formData)
            .then(resCols => {
                const list = resCols || [];
                setCols2(list);
                // Auto-detect default contract column
                const match = list.find(c => {
                    const lc = c.toLowerCase();
                    return lc.includes("contrato") || lc.includes("titulo") || lc.includes("seu_numero") || lc.includes("operacao") || lc.includes("ficha");
                });
                if (match) setContratoCol2(match);
                else if (list.length > 0) setContratoCol2(list[0]);
            })
            .catch(() => {});
    };

    const handleProcessComparison = () => {
        if (!file1) {
            errorAlert("Por favor selecione o Arquivo 1 (Base Anterior / Sistema).");
            return;
        }
        if (!file2) {
            errorAlert("Por favor selecione o Arquivo 2 (Remessa Nova).");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("file1", file1);
        formData.append("file2", file2);
        if (contratoCol1) formData.append("contratoCol1", contratoCol1);
        if (contratoCol2) formData.append("contratoCol2", contratoCol2);

        postUpload("/relatorios/comparativo-base/processar", formData)
            .then(res => {
                setLoading(false);
                setResult(res);
                successAlert(`Comparação concluída com sucesso! ${res?.allRecords?.length || 0} registros analisados.`);
            })
            .catch(err => {
                setLoading(false);
                errorAlert(err?.message || "Erro ao processar e comparar arquivos de remessa.");
            });
    };

    const handleExportExcel = () => {
        if (!file1 || !file2) {
            errorAlert("Selecione ambos os arquivos para exportar.");
            return;
        }

        setExporting(true);
        const formData = new FormData();
        formData.append("file1", file1);
        formData.append("file2", file2);
        if (contratoCol1) formData.append("contratoCol1", contratoCol1);
        if (contratoCol2) formData.append("contratoCol2", contratoCol2);

        postUploadFile("/relatorios/comparativo-base/exportar", formData, "COMPARATIVO_BASE_REMESSAS.xlsx")
            .then(() => {
                setExporting(false);
                successAlert("Planilha comparativa (.xlsx) baixada com sucesso!");
            })
            .catch(err => {
                setExporting(false);
                errorAlert(err?.message || "Erro ao gerar arquivo para download.");
            });
    };

    const displayedRecords = () => {
        if (!result || !result.allRecords) return [];
        let list = result.allRecords;

        if (selectedTab !== "ALL") {
            list = list.filter(r => r.categoria === selectedTab);
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => 
                (r.contrato && r.contrato.toLowerCase().includes(term)) ||
                (r.nomeDevedor && r.nomeDevedor.toLowerCase().includes(term)) ||
                (r.cpfCnpj && r.cpfCnpj.toLowerCase().includes(term))
            );
        }

        return list;
    };

    const cardBg = darkMode ? "#4B4F5C" : "#ffffff";
    const textPrimary = darkMode ? "#ffffff" : "#344767";
    const textSecondary = darkMode ? "#d0d4e0" : "#7b809a";

    const filterCategories = result ? [
        { key: "ALL", label: "Todos", count: result.allRecords?.length || 0, color: "info", icon: "list" },
        { key: "NOVO", label: "Casos Novos", count: result.qtdNovos || 0, color: "success", icon: "add_circle" },
        { key: "REATIVADO", label: "Reativados", count: result.qtdReativados || 0, color: "warning", icon: "autorenew" },
        { key: "MANTIDO_REMESSA", label: "Mantidos (Remessa)", count: result.qtdMantidosRemessa || 0, color: "primary", icon: "published_with_changes" },
        { key: "MANTIDO_ACORDO", label: "Mantidos (Acordo)", count: result.qtdMantidosAcordo || 0, color: "info", icon: "handshake" },
        { key: "DEVOLVIDO", label: "Devolvidos", count: result.qtdDevolvidos || 0, color: "error", icon: "remove_circle" }
    ] : [];

    return (
        <MDBox pt={2} pb={3}>
            {/* 1. SEÇÃO DE CARREGAMENTO E UPLOAD */}
            <Card sx={{ p: 3, mb: 3, background: cardBg }}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: textPrimary }} mb={1}>
                    Comparativo de Base (Remessas)
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: textSecondary }} display="block" mb={3}>
                    Compare a Base Anterior (ou arquivo de origem) com a Remessa Nova para identificar automaticamente novos casos, casos devolvidos, casos mantidos por acordo e reativações.
                </MDTypography>

                <Grid container spacing={3}>
                    {/* ARQUIVO 1 */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2.5,
                                textAlign: "center",
                                borderRadius: 3,
                                borderStyle: "dashed",
                                borderWidth: 2,
                                borderColor: file1 ? "#4CAF50" : (darkMode ? "#6c757d" : "#ccc"),
                                backgroundColor: darkMode ? "#3a3f4d" : "#fafafa",
                                cursor: "pointer",
                                transition: "all 0.2s"
                            }}
                            onClick={() => file1InputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={file1InputRef}
                                style={{ display: "none" }}
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => handleFile1Change(e.target.files[0] || null)}
                            />
                            <Icon fontSize="large" color={file1 ? "success" : "action"}>
                                {file1 ? "check_circle" : "file_upload"}
                            </Icon>
                            <MDTypography variant="subtitle2" fontWeight="bold" mt={1} sx={{ color: textPrimary }}>
                                Arquivo 1 (Base Anterior / Sistema)
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: textSecondary }} display="block">
                                {file1 ? file1.name : "Clique para selecionar o arquivo (.xlsx, .xls, .csv)"}
                            </MDTypography>
                        </Paper>

                        {/* SELECT COLUNA CONTRATO - ARQUIVO 1 */}
                        {cols1.length > 0 && (
                            <MDBox mt={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="lbl-contrato-1" sx={{ color: textSecondary }}>
                                        Campo de Contrato (Arquivo 1)
                                    </InputLabel>
                                    <Select
                                        labelId="lbl-contrato-1"
                                        label="Campo de Contrato (Arquivo 1)"
                                        value={contratoCol1}
                                        onChange={(e) => setContratoCol1(e.target.value)}
                                        sx={{
                                            height: "40px",
                                            color: textPrimary,
                                            ".MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "#606472" : "#ccc"
                                            }
                                        }}
                                    >
                                        {cols1.map((col, i) => (
                                            <MenuItem key={i} value={col}>
                                                {col}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </MDBox>
                        )}
                    </Grid>

                    {/* ARQUIVO 2 */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2.5,
                                textAlign: "center",
                                borderRadius: 3,
                                borderStyle: "dashed",
                                borderWidth: 2,
                                borderColor: file2 ? "#2196F3" : (darkMode ? "#6c757d" : "#ccc"),
                                backgroundColor: darkMode ? "#3a3f4d" : "#fafafa",
                                cursor: "pointer",
                                transition: "all 0.2s"
                            }}
                            onClick={() => file2InputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={file2InputRef}
                                style={{ display: "none" }}
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => handleFile2Change(e.target.files[0] || null)}
                            />
                            <Icon fontSize="large" color={file2 ? "info" : "action"}>
                                {file2 ? "check_circle" : "file_upload"}
                            </Icon>
                            <MDTypography variant="subtitle2" fontWeight="bold" mt={1} sx={{ color: textPrimary }}>
                                Arquivo 2 (Remessa Nova)
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: textSecondary }} display="block">
                                {file2 ? file2.name : "Clique para selecionar o arquivo (.xlsx, .xls, .csv)"}
                            </MDTypography>
                        </Paper>

                        {/* SELECT COLUNA CONTRATO - ARQUIVO 2 */}
                        {cols2.length > 0 && (
                            <MDBox mt={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="lbl-contrato-2" sx={{ color: textSecondary }}>
                                        Campo de Contrato (Arquivo 2)
                                    </InputLabel>
                                    <Select
                                        labelId="lbl-contrato-2"
                                        label="Campo de Contrato (Arquivo 2)"
                                        value={contratoCol2}
                                        onChange={(e) => setContratoCol2(e.target.value)}
                                        sx={{
                                            height: "40px",
                                            color: textPrimary,
                                            ".MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "#606472" : "#ccc"
                                            }
                                        }}
                                    >
                                        {cols2.map((col, i) => (
                                            <MenuItem key={i} value={col}>
                                                {col}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </MDBox>
                        )}
                    </Grid>
                </Grid>

                <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleProcessComparison}
                        disabled={loading || !file1 || !file2}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Icon>compare_arrows</Icon>}
                    >
                        {loading ? "Processando..." : "Processar e Comparar"}
                    </MDButton>

                    {result && (
                        <MDButton
                            variant="gradient"
                            color="success"
                            onClick={handleExportExcel}
                            disabled={exporting}
                            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <Icon>download</Icon>}
                        >
                            {exporting ? "Gerando Excel..." : "Exportar Planilha Completa (.xlsx)"}
                        </MDButton>
                    )}
                </MDBox>
            </Card>

            {/* 2. DASHBOARD DE METRICAS E RESUMO MATEMÁTICO */}
            {result && (
                <>
                    {/* CARDS DE DETALHAMENTO DE CATEGORIAS */}
                    <Grid container spacing={2.5} mb={3} alignItems="stretch">
                        <Grid item xs={12} sm={6} md={3} display="flex">
                            <Card sx={{ p: 2, width: "100%", background: cardBg, borderLeft: "5px solid #4CAF50", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: textSecondary }} textTransform="uppercase">
                                            Casos Novos
                                        </MDTypography>
                                        <MDTypography variant="h3" fontWeight="bold" color="success">
                                            {result.qtdNovos || 0}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem" }}>
                                            Vieram na Remessa Nova
                                        </MDTypography>
                                    </MDBox>
                                    <Icon fontSize="large" color="success">add_circle</Icon>
                                </MDBox>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} display="flex">
                            <Card sx={{ p: 2, width: "100%", background: cardBg, borderLeft: "5px solid #FF9800", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: textSecondary }} textTransform="uppercase">
                                            Casos Reativados
                                        </MDTypography>
                                        <MDTypography variant="h3" fontWeight="bold" color="warning">
                                            {result.qtdReativados || 0}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem" }}>
                                            Reativados na Remessa
                                        </MDTypography>
                                    </MDBox>
                                    <Icon fontSize="large" color="warning">autorenew</Icon>
                                </MDBox>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} display="flex">
                            <Card sx={{ p: 2, width: "100%", background: cardBg, borderLeft: "5px solid #1A73E8", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: textSecondary }} textTransform="uppercase">
                                            Mantidos (Remessa)
                                        </MDTypography>
                                        <MDTypography variant="h3" fontWeight="bold" color="info">
                                            {result.qtdMantidosRemessa || 0}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem" }}>
                                            Atualizados da Base Antiga
                                        </MDTypography>
                                    </MDBox>
                                    <Icon fontSize="large" color="info">published_with_changes</Icon>
                                </MDBox>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} display="flex">
                            <Card sx={{ p: 2, width: "100%", background: cardBg, borderLeft: "5px solid #9C27B0", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: textSecondary }} textTransform="uppercase">
                                            Mantidos (Acordo)
                                        </MDTypography>
                                        <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#9C27B0" }}>
                                            {result.qtdMantidosAcordo || 0}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.72rem" }}>
                                            Retidos por Acordo Ativo
                                        </MDTypography>
                                    </MDBox>
                                    <Icon fontSize="large" sx={{ color: "#9C27B0" }}>handshake</Icon>
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* BANNER DE CONSISTÊNCIA DE TOTAIS PERFEITAMENTE ALINHADO */}
                    <Grid container spacing={2.5} mb={3} alignItems="stretch">
                        {/* CARD 1: BASE ANTERIOR */}
                        <Grid item xs={12} md={4} display="flex">
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    backgroundColor: darkMode ? "#383C48" : "#f0f4f9",
                                    borderRadius: "10px",
                                    border: darkMode ? "1px solid #525663" : "1px solid #d0d7de"
                                }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" height="24px">
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: textSecondary }} textTransform="uppercase">
                                        Base Anterior (Arquivo 1)
                                    </MDTypography>
                                    <Chip label="Origem" color="default" size="small" sx={{ height: "18px", fontSize: "0.68rem" }} />
                                </MDBox>
                                <MDTypography variant="h4" fontWeight="bold" sx={{ color: textPrimary, my: 0.5 }}>
                                    {result.totalBase1 || 0} <MDTypography component="span" variant="button" sx={{ color: textSecondary }}>casos</MDTypography>
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.75rem" }}>
                                    Devolvidos/Baixados da Base: <b>{result.qtdDevolvidos || 0}</b>
                                </MDTypography>
                            </Paper>
                        </Grid>

                        {/* CARD 2: REMESSA NOVA */}
                        <Grid item xs={12} md={4} display="flex">
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    backgroundColor: darkMode ? "#2b3b4e" : "#e3f2fd",
                                    borderRadius: "10px",
                                    border: darkMode ? "1px solid #3b506b" : "1px solid #90caf9"
                                }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" height="24px">
                                    <MDTypography variant="caption" fontWeight="bold" color="info" textTransform="uppercase">
                                        Total Remessa Nova (Arquivo 2)
                                    </MDTypography>
                                    <Chip label="Bate 100%" color="success" size="small" sx={{ height: "18px", fontSize: "0.68rem" }} />
                                </MDBox>
                                <MDTypography variant="h4" fontWeight="bold" color="info" my={0.5}>
                                    {result.totalBase2 || 0} <MDTypography component="span" variant="button" sx={{ color: textSecondary }}>casos</MDTypography>
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.75rem" }}>
                                    Novos ({result.qtdNovos}) + Reativados ({result.qtdReativados}) + Mantidos ({result.qtdMantidosRemessa}) = <b>{result.totalBase2}</b>
                                </MDTypography>
                            </Paper>
                        </Grid>

                        {/* CARD 3: BASE FINAL */}
                        <Grid item xs={12} md={4} display="flex">
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    backgroundColor: darkMode ? "#2e3b30" : "#e8f5e9",
                                    borderRadius: "10px",
                                    border: darkMode ? "1px solid #3e5241" : "1px solid #a5d6a7"
                                }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" height="24px">
                                    <MDTypography variant="caption" fontWeight="bold" color="success" textTransform="uppercase">
                                        Base Atualizada Final no Sistema
                                    </MDTypography>
                                    <Chip label="Final Ativa" color="success" size="small" sx={{ height: "18px", fontSize: "0.68rem" }} />
                                </MDBox>
                                <MDTypography variant="h4" fontWeight="bold" color="success" my={0.5}>
                                    {result.totalBaseFinal || 0} <MDTypography component="span" variant="button" sx={{ color: textSecondary }}>casos</MDTypography>
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: textSecondary, fontSize: "0.75rem" }}>
                                    Remessa Nova ({result.totalBase2}) + Mantidos p/ Acordo ({result.qtdMantidosAcordo}) = <b>{result.totalBaseFinal}</b>
                                </MDTypography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* 3. TABELA DE DETALHES COM FILTROS EM BOTÕES E BUSCA */}
                    <Card sx={{ p: 3, background: cardBg }}>
                        {/* PAINEL DE FILTROS & BUSCA */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                            {/* BOTÕES DE FILTRO POR CATEGORIA */}
                            <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
                                {filterCategories.map(cat => {
                                    const isSelected = selectedTab === cat.key;
                                    return (
                                        <MDBox
                                            key={cat.key}
                                            onClick={() => setSelectedTab(cat.key)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 2,
                                                py: 1,
                                                borderRadius: "10px",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease-in-out",
                                                backgroundColor: isSelected
                                                    ? (darkMode ? "#384152" : "#e3f2fd")
                                                    : (darkMode ? "rgba(255, 255, 255, 0.05)" : "#f8f9fa"),
                                                border: isSelected
                                                    ? (darkMode ? "1px solid #64B5F6" : "1px solid #1976d2")
                                                    : (darkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e0e0e0"),
                                                color: isSelected
                                                    ? (darkMode ? "#ffffff" : "#0d47a1")
                                                    : (darkMode ? "#d0d4e0" : "#555555"),
                                                "&:hover": {
                                                    backgroundColor: isSelected
                                                        ? (darkMode ? "#424d61" : "#bbdefb")
                                                        : (darkMode ? "rgba(255, 255, 255, 0.1)" : "#eeeeee"),
                                                    transform: "translateY(-1px)"
                                                }
                                            }}
                                        >
                                            <Icon fontSize="small" sx={{ color: isSelected ? (darkMode ? "#64B5F6" : "#1976d2") : "inherit" }}>
                                                {cat.icon}
                                            </Icon>
                                            <MDTypography
                                                variant="button"
                                                fontWeight={isSelected ? "bold" : "regular"}
                                                sx={{ color: "inherit", fontSize: "0.85rem" }}
                                            >
                                                {cat.label}
                                            </MDTypography>
                                            <Chip
                                                label={cat.count}
                                                size="small"
                                                color={isSelected ? cat.color : "default"}
                                                sx={{
                                                    height: "22px",
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold",
                                                    backgroundColor: isSelected ? undefined : (darkMode ? "rgba(255, 255, 255, 0.15)" : "#e0e0e0"),
                                                    color: isSelected ? "#ffffff" : (darkMode ? "#ffffff" : "#333333")
                                                }}
                                            />
                                        </MDBox>
                                    );
                                })}
                            </MDBox>

                            {/* BUSCA RÁPIDA */}
                            <MDBox width={{ xs: "100%", sm: "340px" }}>
                                <MDInput
                                    size="small"
                                    fullWidth
                                    placeholder="Buscar por contrato, CPF, devedor..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    icon={{ component: "search", direction: "left" }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "40px"
                                        },
                                        "& .MuiInputBase-input": {
                                            color: textPrimary,
                                            fontSize: "0.85rem",
                                            pr: 1
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                            color: textSecondary,
                                            opacity: 1,
                                            fontSize: "0.82rem"
                                        }
                                    }}
                                />
                            </MDBox>
                        </MDBox>

                        {/* TABELA DE REGISTROS (SEM A COLUNA CARTEIRA) */}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                backgroundColor: darkMode ? "#424550" : "#ffffff",
                                borderRadius: "8px",
                                border: darkMode ? "1px solid #606472" : "1px solid #e0e0e0",
                                overflowX: "auto"
                            }}
                        >
                            <Table size="small" sx={{ width: "100%", minWidth: "1050px", borderCollapse: "collapse" }}>
                                <TableHead sx={{ display: "table-header-group", width: "100%" }}>
                                    <TableRow sx={{ display: "table-row", width: "100%" }}>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "50px" }}>#</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "160px" }}>Categoria</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "160px" }}>Contrato</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "160px" }}>CPF/CNPJ</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "280px" }}>Devedor</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0", width: "140px" }}>Valor (R$)</TableCell>
                                        <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? "#383C48 !important" : "#f8f9fa !important", color: darkMode ? "#FFFFFF !important" : "#344767 !important", fontWeight: "bold", fontSize: "0.8rem", py: 1.5, px: 2, verticalAlign: "top", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #606472" : "1px solid #e0e0e0" }}>Observação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                                    {displayedRecords().length === 0 ? (
                                        <TableRow sx={{ display: "table-row" }}>
                                            <TableCell colSpan={7} align="center" sx={{ display: "table-cell", py: 3, color: textSecondary }}>
                                                Nenhum caso encontrado para o filtro selecionado.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayedRecords().map((rec, idx) => {
                                            let chipColor = "default";
                                            let labelText = rec.categoria;
                                            if (rec.categoria === "NOVO") { chipColor = "success"; labelText = "Caso Novo"; }
                                            else if (rec.categoria === "DEVOLVIDO") { chipColor = "error"; labelText = "Devolvido"; }
                                            else if (rec.categoria === "MANTIDO_ACORDO") { chipColor = "info"; labelText = "Mantido (Acordo)"; }
                                            else if (rec.categoria === "MANTIDO_REMESSA") { chipColor = "primary"; labelText = "Mantido (Remessa)"; }
                                            else if (rec.categoria === "REATIVADO") { chipColor = "warning"; labelText = "Reativado"; }

                                            return (
                                                <TableRow key={idx} sx={{ display: "table-row", "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" } }}>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", color: textPrimary, fontSize: "0.85rem", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{idx + 1}</TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>
                                                        <Chip label={labelText} color={chipColor} size="small" sx={{ fontWeight: "bold", fontSize: "0.75rem" }} />
                                                    </TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", fontWeight: "bold", color: textPrimary, fontSize: "0.85rem", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{rec.contrato || "-"}</TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", color: textPrimary, fontSize: "0.85rem", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{rec.cpfCnpj || "-"}</TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", color: textPrimary, fontSize: "0.85rem", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{rec.nomeDevedor || "-"}</TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", color: textPrimary, fontSize: "0.85rem", fontWeight: "500", whiteSpace: "nowrap", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{rec.valorStr || "0,00"}</TableCell>
                                                    <TableCell sx={{ display: "table-cell", py: 1.5, px: 2, verticalAlign: "top", color: textSecondary, fontSize: "0.82rem", borderBottom: darkMode ? "1px solid #525663" : "1px solid #e0e0e0" }}>{rec.observacao || "-"}</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}
        </MDBox>
    );
}
